import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { EmptyState } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { useDailySalesSeriesQuery, useRevenuePeriodsQuery } from '../api';
import { AlertsPanel } from '../components/AlertsPanel';
import { KpiCard, type KpiTrend } from '../components/KpiCard';
import { QuickActions } from '../components/QuickActions';
import { SalesTrendChart } from '../components/SalesTrendChart';
import { StatusBreakdownBars } from '../components/StatusBreakdownBars';
import { TopProducts } from '../components/TopProducts';
import { WelcomeCard } from '../components/WelcomeCard';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useMerchantAnalyticsQuery } from '@/features/analytics/api';
import { formatCatalogPrice } from '@/features/products/format-price';
import { ShareStoreCard } from '@/features/share/components/ShareStoreCard';
import { whenDefined } from '@/lib/optional-props';

const MONEY_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3v18M17 7.5c0-1.8-2.2-3-5-3s-5 1.2-5 3 2.2 3 5 3 5 1.2 5 3-2.2 3-5 3-5-1.2-5-3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const RECEIPT_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 3h12v18l-2.5-1.5L13 21l-2.5-1.5L8 21l-2-1.5V3Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M9 8h6M9 12h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const BOX_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3.5 8 12 3.5 20.5 8v8L12 20.5 3.5 16V8Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M3.5 8 12 12.5 20.5 8M12 12.5V20.5" stroke="currentColor" strokeWidth="2" />
  </svg>
);

function computeTodayTrend(
  points: ReturnType<typeof useDailySalesSeriesQuery>['points'],
  t: ReturnType<typeof useI18n>['t'],
): KpiTrend | undefined {
  const today = points.at(-1);
  const yesterday = points.at(-2);
  if (today === undefined || yesterday === undefined) return undefined;

  if (yesterday.revenue > 0) {
    const changePercent = ((today.revenue - yesterday.revenue) / yesterday.revenue) * 100;
    const rounded = Math.round(changePercent);
    if (rounded === 0) return { direction: 'flat', label: t('merchant.dashboard.trendStable') };
    return {
      direction: rounded > 0 ? 'up' : 'down',
      label: t('merchant.dashboard.trendChange', {
        value: `${rounded > 0 ? '+' : ''}${String(rounded)}`,
      }),
    };
  }
  if (today.revenue > 0) {
    return { direction: 'up', label: t('merchant.dashboard.trendNewActivity') };
  }
  return undefined;
}

/**
 * Sprint UX/UI Merchant Dashboard V2 — full rewrite. Every one of the four
 * KPI cards that used to render "Bientôt" now shows real data: `revenue`
 * (CA du jour / CA du mois) computed client-side from real orders
 * (explicitly authorized — see features/dashboard/api.ts's own header),
 * `Nombre de commandes` / `Produits actifs` straight from
 * `GET /merchant/analytics` (S17). The history view lives in
 * `AnalyticsPage.tsx` (« Activité récente ») — this screen is the merchant's daily "how is my
 * shop doing" view, not a technical audit tool (supervisor's own framing).
 */
export function DashboardPage(): ReactElement {
  const { t } = useI18n();
  const analyticsQuery = useMerchantAnalyticsQuery();
  const revenuePeriods = useRevenuePeriodsQuery();
  const dailySeries = useDailySalesSeriesQuery();

  if (analyticsQuery.isPending) {
    return (
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <WelcomeCard />
        <TableSkeleton columns={4} />
      </div>
    );
  }

  if (analyticsQuery.isError) {
    return (
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <WelcomeCard />
        <EmptyState
          title={
            analyticsQuery.error.status === 403
              ? t('merchant.common.accessDenied')
              : t('merchant.dashboard.loadError')
          }
          description={
            analyticsQuery.error.status === 403
              ? t('merchant.dashboard.accessDeniedDescription')
              : t(resolveErrorMessageKey(analyticsQuery.error))
          }
        />
      </div>
    );
  }

  const analytics = analyticsQuery.data;
  const todayTrend = computeTodayTrend(dailySeries.points, t);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <WelcomeCard />
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">
          {t('merchant.dashboard.title')}
        </h1>
        <p className="text-sm text-inkFaint">{t('merchant.dashboard.subtitle')}</p>
      </div>

      <ShareStoreCard context="dashboard" />

      <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 sm:gap-4 xl:grid-cols-4">
        <KpiCard
          label={t('merchant.dashboard.kpiTodayRevenue')}
          value={revenuePeriods.isPending ? '…' : formatCatalogPrice(revenuePeriods.todayRevenue)}
          icon={MONEY_ICON}
          tone="accent"
          {...whenDefined(
            'hint',
            revenuePeriods.isPending
              ? undefined
              : t('merchant.dashboard.kpiTodayHint', { count: revenuePeriods.todayOrderCount }),
          )}
          {...whenDefined('trend', todayTrend)}
        />
        <KpiCard
          label={t('merchant.dashboard.kpiMonthRevenue')}
          value={revenuePeriods.isPending ? '…' : formatCatalogPrice(revenuePeriods.monthRevenue)}
          icon={MONEY_ICON}
          tone="accent"
          {...whenDefined(
            'hint',
            revenuePeriods.isPending
              ? undefined
              : t('merchant.dashboard.kpiMonthHint', { count: revenuePeriods.monthOrderCount }),
          )}
        />
        <KpiCard
          label={t('merchant.dashboard.kpiOrderCount')}
          value={String(analytics.totalOrderCount)}
          icon={RECEIPT_ICON}
          tone="info"
          hint={t('merchant.dashboard.kpiOrderCountHint')}
        />
        <KpiCard
          label={t('merchant.dashboard.kpiActiveProducts')}
          value={String(analytics.activeProductCount)}
          icon={BOX_ICON}
          tone="good"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <SalesTrendChart />
        </div>
        <div className="rounded-xl2 border border-line bg-surface p-5 shadow-card">
          <h3 className="font-display text-sm font-bold text-ink">
            {t('merchant.dashboard.statusByStatusTitle')}
          </h3>
          <p className="mt-0.5 text-xs text-inkFaint">
            {t('merchant.dashboard.statusByStatusSubtitle')}
          </p>
          <div className="mt-4">
            <StatusBreakdownBars entries={analytics.ordersByStatus} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <AlertsPanel />
        <QuickActions />
      </div>

      <TopProducts products={analytics.topProducts} />
    </div>
  );
}
